import { AfterViewChecked, Component, ContentChildren, ElementRef, Input, OnDestroy, QueryList, ViewChild } from "@angular/core";

import Splide, { Options } from "@splidejs/splide";

import { theme } from "@vattenfall/design-system";

import { defaultExtension } from "./extensions/default.extension";
import { CarouselSlideComponent } from "./slide/carousel-slide.component";
import { createSlideTransition } from "./transitions/create-slide-transition";

type ExtendedOptions = Options & {
  slideTransition?: string;
};

/** Type describing carousel variations */
export type CarouselType = "max-3-slides";

/** Spline configuration object for the different types */
const TYPES: Readonly<{ [key in CarouselType]: Partial<ExtendedOptions> }> = <const>{
  "max-3-slides": {
    autoWidth: false,
    mediaQuery: "min",
    breakpoints: {
      [parseInt(theme.screens["xs"], 10)]: {
        perPage: 1,
        padding: "calc(32px - 8px)" /* 32px: content margin, 16px: slides gap */,
      },
      [parseInt(theme.screens["sm"], 10)]: {
        perPage: 2,
        padding: "calc(32px - 8px)" /* 32px: content margin, 16px: slides gap */,
      },
      [parseInt(theme.screens["md"], 10)]: {
        perPage: 2,
        padding: "calc(72px - 14px)" /* 74px: content margin, 14px: slides gap */,
      },
      [parseInt(theme.screens["lg"], 10)]: {
        perPage: 3,
        padding: "calc(72px - 14px)" /* 60px: content margin, 14px: slides gap */,
      },
      [parseInt(theme.screens["xl"], 10)]: {
        perPage: 3,
        padding: "calc(((100% - 1320px) / 2) - 22px)" /* 1320: content max-width, 22px: slides gap */,
      },
    },
    slideTransition: "transform 300ms cubic-bezier(.6,0,0,1)",
  },
};

/**
 * Vattenfall UI `ui-carousel` is a container for rendering a carousel with slides. Slides are organized into pages,
 * where each page may have one or more slides.
 *
 * This component is a wrapper for [SplideJs](https://splidejs.com/).
 *
 * The provided configuration `max-3-slides` displays a variable number of pages (but never more than three), depending
 * on the current screen size. It also provides a basic slide transition. Apart from this, it mostly relies on default
 * SplideJs options.
 *
 * More configurations may be added.
 *
 * [SplideJs Options](https://splidejs.com/guides/options/).
 */
@Component({
  selector: "ui-carousel",
  styleUrls: ["./carousel.component.scss"],
  templateUrl: "./carousel.component.html",
})
export class CarouselComponent implements AfterViewChecked, OnDestroy {
  @Input() type: CarouselType = "max-3-slides";
  @ViewChild("splideTarget", { static: true }) private readonly splideTarget?: ElementRef<HTMLElement>;
  @ContentChildren(CarouselSlideComponent, { descendants: true }) readonly slides?: QueryList<CarouselSlideComponent>;

  private splideInstance?: Splide;

  ngAfterViewChecked(): void {
    if (this.splideInstance) return;

    const options = TYPES[this.type];
    const transition = createSlideTransition(options.slideTransition);
    const target = this.splideTarget?.nativeElement;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.splideInstance = new Splide(target!, options).mount({ defaultExtension }, transition);
  }

  ngOnDestroy(): void {
    this.splideInstance?.destroy(true);
  }
}
